import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Input } from '@/src/components/ui/input'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { ArrowCounterClockwiseIcon, ArrowsLeftRightIcon, TrashIcon } from '@phosphor-icons/react'
import { ViewAction } from '@/src/components/manage/actions/ViewAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { FilterAction } from '@/src/components/manage/actions/FilterAction'
import { Pagination } from '@/src/components/manage/Pagination'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/src/components/ui/dialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select'
import { listDrawings, rollDrawingShareId, deleteDrawingHard } from '@/src/api/drawing'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { Badge } from '@/src/components/ui/badge'
import { useTranslation } from '@/src/i18n/client'
import { useAuth } from '@/src/components/AuthContextProvider'
import type { Drawing } from '@/shared/types/drawing'
import type { DrawingListItem } from '@/src/types/drawing-list-item'
import { FilePreviewPopover } from '@/src/components/file/FilePreviewPopover'
import { resolveDrawingName, resolveDrawingNumber } from '@/src/lib/drawingHelpers'
import { getSignedUrl } from '@/src/api/file'
import { IsometricIcon } from '@/src/components/manage/tables/IsometricIcon';
import { toast } from 'sonner'
import { ConfirmModal } from '@/src/components/ui/confirm-modal'
import { DateTimeText } from '@/src/components/ui/date-time'

const PAGE_SIZE = 20

const searchParamsSchema = z.object({
  fileId: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_manage/manage/admin/drawings/')({
  component: AdminDrawingsPage,
  validateSearch: (search) => searchParamsSchema.parse(search),
})

function AdminDrawingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient()
  const { user, isAdmin } = useAuth()
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const [search, setSearch] = useState('')
  const [userId, setUserId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [fileId, setFileId] = useState(searchParams.fileId || '')
  const page = (searchParams.page ?? 1) - 1
  const setPage = (nextPage: number) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage > 0 ? nextPage + 1 : undefined,
      }),
    })
  }
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedDrawing, setSelectedDrawing] = useState<any>(null)
  const [hoveredFile, setHoveredFile] = useState<{ fileId: string; thumbnailKey: string | null; position: { x: number; y: number } } | null>(null)
  const [searchType, setSearchType] = useState<'user' | 'project' | 'file' | 'search'>(searchParams.fileId ? 'file' : 'search')
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'createdAt',
    direction: 'desc',
  })
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)

  const navigateToMergeWithSelection = () => {
    const [leftId, rightId] = selectedItems
    void navigate({
      to: '/manage/merge',
      search: {
        leftId,
        rightId,
      },
    })
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['drawing', 'listAll', { search, userId, projectId, fileId, page, sort }],
    queryFn: () => listDrawings({
      data: {
        all: true,
        search: search || undefined,
        userId: userId || undefined,
        projectId: projectId || undefined,
        fileId: fileId || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        orderBy: sort.key,
        order: sort.direction,
      },
    }),
  })

  const drawings = data?.drawings ?? []
  const total = data?.total ?? 0
  const pageCount = Math.ceil(total / PAGE_SIZE)

  const rollShareIdMutation = useMutation({
    mutationFn: (drawingId: string) => rollDrawingShareId({ data: { drawingId } }),
  })

  const deleteDrawingMutation = useMutation({
    mutationFn: (drawingId: string) => deleteDrawingHard({ data: { drawingId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drawing', 'listAll'] })
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (drawingIds: string[]) => {
      const results = await Promise.allSettled(
        drawingIds.map(drawingId => deleteDrawingHard({ data: { drawingId } }))
      )
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) throw new Error(`Failed to delete ${failed} of ${drawingIds.length} items`)
      return results
    },
    onSuccess: (_, drawingIds) => {
      toast.success(t('table.bulkDeleteSuccess', { count: drawingIds.length }))
      setSelectedItems([])
      setShowBulkDeleteConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['drawing', 'listAll'] })
    },
    onError: () => {
      toast.error(t('table.bulkDeleteFailed'))
    },
  })

  const columns: ColumnConfig<DrawingListItem>[] = [
    {
      key: 'shareId',
      label: t('table.shareId'),
      width: 'w-24',
      render: (drawing) => {
        const canRotate = isAdmin || (!!drawing.user?.id && drawing.user.id === user?.id)
        return (
          <ShareLinkCell
            shareId={drawing.shareId}
            reserveActionSpace={true}
            canRotate={canRotate}
            onRotate={
              canRotate
                ? async () => {
                    await rollShareIdMutation.mutateAsync(drawing.id)
                    await queryClient.invalidateQueries({ queryKey: ['drawing', 'listAll'] })
                  }
                : undefined
            }
          />
        )
      },
    },
    {
      key: 'isometric',
      label: '',
      width: 'w-10',
      render: (drawing) => {
        if (!drawing.fileId) return null;
        return <IsometricIcon fileId={drawing.fileId} />;
      },
    },
    {
      key: 'name',
      label: t('table.name'),
      sortable: true,
      primary: true,
      render: (drawing) => {
        const name = resolveDrawingName(drawing)
        return (
          <div
            className="relative inline-flex items-center group"
            onMouseEnter={(e) => {
              if (!drawing.fileId || !drawing.thumbnailLargeUrl) return
              const rect = e.currentTarget.getBoundingClientRect()
              setHoveredFile({
                fileId: drawing.fileId,
                thumbnailKey: drawing.thumbnailLargeUrl,
                position: { x: rect.right + 8, y: rect.top }
              })
            }}
            onMouseLeave={() => {
              setHoveredFile(null)
            }}
          >
            <div className="max-w-45 truncate" title={name || t('table.untitled')}>
              {name ? name : <span className="text-muted-foreground">{t('table.untitled')}</span>}
            </div>
          </div>
        )
      },
    },
        {
      key: 'number',
      label: t('table.drawingNumber'),
      width: 'w-36',
      sortable: true,
      optional: true,  // Hide on smaller screens
      render: (drawing) => resolveDrawingNumber(drawing) || '-',
        },
    {
      key: 'company',
      label: t('table.company'),
      width: 'w-46',
      sortable: true,
      truncate: true,  // Truncate long company names
      render: (drawing) => (drawing.master?.data as any)?.company || '-',
    },
    {
      key: 'user',
      label: t('table.user'),
      width: 'w-40',
      sortable: true,
      truncate: true,  // Truncate long user names
      render: (drawing) => drawing.user?.name || drawing.user?.id || '-',
    },
    {
      key: 'privacy',
      label: t('table.privacy'),
      width: 'w-20',
      sortable: true,
      render: (drawing) => (
        <Badge
          variant={
            drawing.privacy === "LOW"
              ? "default"
              : drawing.privacy === "MEDIUM"
                ? "secondary"
                : "outline"
          }
          className={
            drawing.privacy === "LOW"
              ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-green-100 text-green-800 border-green-200"
              : drawing.privacy === "MEDIUM"
                ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-yellow-100 text-yellow-800 border-yellow-200"
                : "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-red-100 text-red-800 border-red-200"
          }
        >
          {drawing.privacy}
        </Badge>
      ),
    },
  ];

  function handleRowClick(drawing: any) {
    setSelectedDrawing({
      ...drawing,
      user: drawing.files?.[0]?.uploadedBy
        ? { id: drawing.files[0].uploadedBy.id, name: drawing.files[0].uploadedBy.name }
        : null,
      projectId: drawing.projects?.[0]?.id ?? null,
    })
    setDialogOpen(true)
  }

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar selectedCount={selectedItems.length}>
        <AdminToolbar.BulkActions>
          {selectedItems.length === 2 && (
            <Button
              variant="outline"
              size="lg"
              onClick={navigateToMergeWithSelection}
            >
              <ArrowsLeftRightIcon className="w-4 h-4 mr-2" />
              {t('table.bulkMerge')}
            </Button>
          )}
          <Button
            variant="destructive"
            size="lg"
            onClick={() => setShowBulkDeleteConfirm(true)}
            disabled={bulkDeleteMutation.isPending}
          >
            <TrashIcon className="w-4 h-4 mr-2" />
            {t('table.bulkDelete')}
          </Button>
        </AdminToolbar.BulkActions>
        <AdminToolbar.Right>
          <Pagination
            page={page}
            totalPages={pageCount}
            onPageChange={setPage}
            compact
          />
          <FilterAction>
            <div className="flex flex-col gap-3">
              <Select
                value={userId ? 'user' : projectId ? 'project' : fileId ? 'file' : (search ? 'search' : 'search')}
                onValueChange={val => {
                  if (val === 'user') {
                    setSearchType('user')
                    setUserId('')
                    setProjectId('')
                    setFileId('')
                    setSearch('')
                  } else if (val === 'project') {
                    setSearchType('project')
                    setUserId('')
                    setProjectId('')
                    setFileId('')
                    setSearch('')
                  } else if (val === 'file') {
                    setSearchType('file')
                    setUserId('')
                    setProjectId('')
                    setFileId('')
                    setSearch('')
                  } else {
                    setSearchType('search')
                    setUserId('')
                    setProjectId('')
                    setFileId('')
                    setSearch('')
                  }
                  setPage(0)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Search or Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="search">Search</SelectItem>
                  <SelectItem value="user">Filter User</SelectItem>
                  <SelectItem value="project">Filter Project</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder={searchType === 'search' ? 'Search drawings' : searchType === 'user' ? 'User ID' : searchType === 'project' ? 'Project ID' : 'File ID'}
                value={userId || projectId || fileId || search}
                onChange={e => {
                  if (searchType === 'user') {
                    setUserId(e.target.value)
                  } else if (searchType === 'project') {
                    setProjectId(e.target.value)
                  } else if (searchType === 'file') {
                    setFileId(e.target.value)
                  } else {
                    setSearch(e.target.value)
                  }
                  setPage(0)
                }}
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    setSearch('')
                    setUserId('')
                    setProjectId('')
                    setFileId('')
                    setPage(0)
                  }}
                  aria-label="Reset"
                >
                  <ArrowCounterClockwiseIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </FilterAction>
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="mt-4">
        <DataTable<DrawingListItem>
          data={drawings}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('table.noDrawings')}
          sort={sort}
          onSortChange={(next) => {
            setSort(next ?? { key: 'createdAt', direction: 'desc' })
          }}
          selectable={true}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          actions={(drawing) => (
            <>
              <ViewAction
                to={`/manage/admin/drawings/${drawing.id}`}
              />
              <DeleteAction
                itemName={drawing.name || 'this drawing'}
                itemType="drawing"
                onDelete={async () => {
                  await deleteDrawingMutation.mutateAsync(drawing.id)
                }}
                isPending={deleteDrawingMutation.isPending}
                customDescription={t('table.confirmDeleteDrawingPermanent')} 
              />
            </>
          )}
        />
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-screen md:min-w-2xl max-w-full rounded-none sm:rounded-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Drawing Details</DialogTitle>
            <DialogClose />
          </DialogHeader>
          {selectedDrawing && (
            <div className="space-y-4 text-sm overflow-y-auto pr-2 flex-1">
              <div><span className="font-semibold">ID:</span> {selectedDrawing.id}</div>
              <div><span className="font-semibold">Share ID:</span> {selectedDrawing.shareId || '-'}</div>
              <div><span className="font-semibold">Name:</span> {selectedDrawing.name || '-'}</div>
              <div>
                <span className="font-semibold">User:</span>{' '}
                {selectedDrawing.user && selectedDrawing.user.id
                  ? (
                    <span
                      onClick={() => {
                        setUserId(selectedDrawing.user.id)
                        setDialogOpen(false)
                        setPage(0)
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      title={selectedDrawing.user.name || selectedDrawing.user.id}
                    >
                      {selectedDrawing.user.name || selectedDrawing.user.id}
                    </span>
                  )
                  : '-'}
              </div>
              <div>
                <span className="font-semibold">Project:</span>{' '}
                {selectedDrawing.projectId
                  ? (
                    <span
                      onClick={() => {
                        setProjectId(selectedDrawing.projectId)
                        setDialogOpen(false)
                        setPage(0)
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      title={selectedDrawing.projectId}
                    >
                      {selectedDrawing.projectId}
                    </span>
                  )
                  : '-'}
              </div>
              <div><span className="font-semibold">Date:</span> <DateTimeText>{selectedDrawing.createdAt}</DateTimeText></div>
              {selectedDrawing.content && (
                <div>
                  <span className="font-semibold">Content:</span>
                  <pre className="bg-muted rounded p-2 mt-1 overflow-x-auto text-xs">
                    {JSON.stringify(selectedDrawing.content, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <FilePreviewPopover
        fileId={hoveredFile?.fileId ?? ''}
        thumbnailKey={hoveredFile?.thumbnailKey ?? null}
        position={hoveredFile?.position ?? { x: 0, y: 0 }}
        isVisible={!!hoveredFile}
        onClose={() => setHoveredFile(null)}
      />
      <ConfirmModal
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        title={t('table.bulkDeleteConfirmTitle', { count: selectedItems.length })}
        description={t('table.bulkDeleteConfirmDescription', { count: selectedItems.length })}
        confirmLabel={t('action.delete')}
        cancelLabel={t('action.cancel')}
        loadingLabel={t('action.deleting')}
        variant="destructive"
        onConfirm={() => bulkDeleteMutation.mutate(selectedItems)}
        isLoading={bulkDeleteMutation.isPending}
      />
    </div>
  )
}
